// Players for links that can play inside the chat (YouTube, Vimeo, Spotify…),
// worked out from the URL alone, so a sender can't make the receiver load an
// arbitrary page. Nothing loads until the user taps play.

export interface Embed {
  provider: "youtube" | "vimeo" | "spotify" | "dailymotion" | "loom";
  src: string; // iframe URL (autoplays: it's only created on tap)
  /** width / height of the player; Spotify has a fixed height instead */
  aspect?: number;
  height?: number;
}

const ID = /^[\w-]{6,64}$/;

/** "1m30s" / "90" / "90s" -> seconds */
function seconds(t: string | null): number {
  if (!t) return 0;
  if (/^\d+$/.test(t)) return Number(t);
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(t);
  return m ? Number(m[1] ?? 0) * 3600 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0) : 0;
}

export function embedFor(raw: string): Embed | null {
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  const host = u.hostname.replace(/^(www|m)\./, "").toLowerCase();
  const path = u.pathname.split("/").filter(Boolean);

  if (host === "youtube.com" || host === "music.youtube.com" || host === "youtu.be" || host === "youtube-nocookie.com") {
    let id = "";
    let shorts = false;
    if (host === "youtu.be") id = path[0] ?? "";
    else if (path[0] === "watch") id = u.searchParams.get("v") ?? "";
    else if (["embed", "live", "v", "shorts"].includes(path[0])) (id = path[1] ?? ""), (shorts = path[0] === "shorts");
    if (!/^[\w-]{11}$/.test(id)) return null;
    const start = seconds(u.searchParams.get("t") ?? u.searchParams.get("start"));
    // youtube-nocookie: no tracking cookies until the video plays.
    return { provider: "youtube", src: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0${start ? `&start=${start}` : ""}`, aspect: shorts ? 9 / 16 : 16 / 9 };
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const id = path.find((p) => /^\d{5,12}$/.test(p));
    return id ? { provider: "vimeo", src: `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`, aspect: 16 / 9 } : null;
  }
  if (host === "dailymotion.com" || host === "dai.ly") {
    const id = host === "dai.ly" ? path[0] : path[0] === "video" ? path[1] : undefined;
    return id && ID.test(id) ? { provider: "dailymotion", src: `https://www.dailymotion.com/embed/video/${id}?autoplay=1`, aspect: 16 / 9 } : null;
  }
  if (host === "open.spotify.com") {
    // /track/ID, /album/ID, /playlist/ID, /episode/ID, /show/ID (also /intl-xx/track/ID)
    const at = path.findIndex((p) => ["track", "album", "playlist", "episode", "show", "artist"].includes(p));
    const type = path[at];
    const id = path[at + 1];
    if (at < 0 || !id || !/^[A-Za-z0-9]{10,40}$/.test(id)) return null;
    return { provider: "spotify", src: `https://open.spotify.com/embed/${type}/${id}?autoplay=1`, height: type === "track" || type === "episode" ? 152 : 352 };
  }
  if (host === "loom.com" && path[0] === "share" && path[1] && ID.test(path[1])) {
    return { provider: "loom", src: `https://www.loom.com/embed/${path[1]}?autoplay=1`, aspect: 16 / 9 };
  }
  return null;
}
